import { createAction, createReducer, Reducer } from '@reduxjs/toolkit';
import { getOppositeSide } from '../../rfqs/quotes-functions';

export type TwoWayValue = [bidPrice: string, askPrice: string];

export interface QuoteBuilderState {
  account: string | undefined;
  isPostOnly: boolean;
  quoteTimer: string | null;
  quantity: string;
  price: string;
  legsPrice: readonly TwoWayValue[];
  side: 'BUY' | 'SELL';
}

export const emptyTwoWay: TwoWayValue = ['', ''];

export const INITIAL_STATE: QuoteBuilderState = {
  account: undefined,
  isPostOnly: false,
  quoteTimer: null,
  quantity: '',
  price: '',
  legsPrice: [],
  side: 'BUY',
};

export interface ChangeLegPriceArgs {
  readonly value: string;
  readonly index: number;
  readonly side: 'BUY' | 'SELL';
}

export const changeAccount = createAction<string>('changeAccount');
export const changeIsPostOnly = createAction<boolean>('changeIsPostOnly');
export const changeQuoteTimer = createAction<string>('changeQuoteTimer');
export const changeQuantity = createAction<string>('changeQuantity');
export const changePrice = createAction<string>('changePrice');
export const changeInputLegPrice = createAction<TwoWayValue[]>(
  'changeInputLegPrice',
);
export const changeLegPrice =
  createAction<ChangeLegPriceArgs>('changeLegPrice');
export const toggleSide = createAction('toggleSide');
export const switchLegPrices = createAction('switchLegPrices');

const reducer: Reducer<QuoteBuilderState> = createReducer(
  INITIAL_STATE,
  (builder) =>
    builder
      .addCase(changeAccount, (state, action) => {
        state.account = action.payload;
      })
      .addCase(changeIsPostOnly, (state, action) => {
        state.isPostOnly = action.payload;
      })
      .addCase(changeQuoteTimer, (state, action) => {
        state.quoteTimer = action.payload;
      })
      .addCase(changeQuantity, (state, action) => {
        state.quantity = action.payload;
      })
      .addCase(changePrice, (state, action) => {
        state.price = action.payload;
      })
      .addCase(changeInputLegPrice, (state, { payload }) => {
        state.legsPrice = payload;
      })
      .addCase(toggleSide, (state) => {
        state.side = getOppositeSide(state.side);
      })
      .addCase(switchLegPrices, (state) => {
        const switchedLegsPrice = state.legsPrice.map(
          ([bidPrice, askPrice]) => [askPrice, bidPrice] as TwoWayValue,
        );

        state.legsPrice = switchedLegsPrice;
      })
      .addCase(changeLegPrice, (state, { payload }) => {
        state.legsPrice.map((price, index) => {
          if (payload.index !== index) return price;

          const updatedPrice = state.legsPrice[payload.index];
          if (updatedPrice == null) return price;

          updatedPrice[payload.side === 'BUY' ? 0 : 1] = payload.value;
          return updatedPrice;
        });
      }),
);

export default reducer;

export type QuoteBuilderReducer = typeof reducer;
